import React, {useEffect, useRef, useState} from 'react';
import {addIdToHash, removeIdFromHash, saveCheck} from '../../functions/Global';
import CustomDialog, {CustomDialogHeaderType} from '../../global/CustomDialog';
import {Button} from 'primereact/button';
import {PersonEditForm} from './PersonEditForm';
import {resetMappings} from '../../../redux/slices/MappingSlice';
import {useAppDispatch} from '../../../redux/hooks';
import {useDeletePersonMutation, useGetOutlookContactMutation} from '../../../redux/rtk/injectedPersonApi';
import {useTranslation} from 'react-i18next';
import {CustomConfirmDialog} from '../../global/CustomConfirmDialog';
import {deleteRole} from '../../functions/UserManagement';
import {showMessageOnError, showMessageOnSuccess} from '../../global/CustomToast';
import {Menu} from 'primereact/menu';
import {Person} from '../../functions/GlobalData';
import {ProcessUser} from '../process/ProcessUser';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPen, faPlus} from '@fortawesome/free-solid-svg-icons';
import {permissions} from '../../../config/permissions';
import {checkPermission} from '../../../functions/functionLibrary';

type Props = {
    projectId: string,
    commonData: boolean,
    geoData: any,
    onFinished?: (success: any) => void,
    selectedPerson?: Person,
    setSelectedPerson?: any,
    show?: boolean,
    setShow?: (data: boolean) => void,
}
export const PersonDialogEditForm = (props: Props): JSX.Element => {

    const {t} = useTranslation(['common']);
    const menuActions = useRef<any>(null);
    const menuOptions = useRef<any>(null);

    const formName = 'editPersonForm';
    const dispatch = useAppDispatch();

    const [deletePerson, resultDeletePerson] = useDeletePersonMutation();

    const [getOutlookContact, resultGetOutlookContact] = useGetOutlookContactMutation();
    const [buttons, setButtons] = useState<any[]>([]);
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [close, setClose] = useState<boolean>(false);
    const [createNew, setCreateNew] = useState<boolean>(false);
    const [duplicateNew, setDuplicateNew] = useState<boolean>(false);

    useEffect(() => {
        if (props.selectedPerson) {
            addIdToHash(props.selectedPerson.person_id);
        }
    }, [props.selectedPerson]);

    useEffect(() => {
        setButtons(getButtons());
    }, [isDirty]);

    useEffect(() => {
        if (createNew) {
            setCreateNew(false);
            props.setSelectedPerson(undefined);
        }
    }, [createNew]);

    useEffect(() => {
        if (duplicateNew) {
            setDuplicateNew(false);
            props.setSelectedPerson(undefined);
        }
    }, [duplicateNew]);

    const getButtons = () => {
        let result: any[] = [];

        let itemsActions: any[] = [];
        let itemsOptions: any[] = [];

        itemsActions.push({
            label: t('globalData:persons.create'),
            icon: 'pi pi-plus',
            command: () => {
                if (isDirty) {
                    showMessageOnError(t('globalData:toasts.saveDataFirstHeader'), t('globalData:toasts.saveDataFirstMessage'));
                    return;
                }
                setCreateNew(true);
            }
        });

        itemsActions.push({
            label: t('globalData:persons.duplicate'),
            icon: 'pi pi-copy',
            command: () => {
                if (isDirty) {
                    showMessageOnError(t('globalData:toasts.saveDataFirstHeader'), t('globalData:toasts.saveDataFirstMessage'));
                    return;
                }

                setDuplicateNew(true);
            }
        });

        if (props.selectedPerson) {
            itemsActions.push({
                label: t('delete'),
                icon: 'pi pi-trash',
                command: () => {
                    CustomConfirmDialog({
                        message: t('globalData:dialogs.deletePersonDialog.singular'),
                        header: t('confirmDialog:deleteDialogHeader'),
                        translation: t,
                        onConfirm: () => {
                            if (props.selectedPerson) {
                                deletePerson({
                                    project_id: props.projectId ? props.projectId : '',
                                    person_id: props.selectedPerson.person_id,
                                    common_data: true
                                });
                                dispatch(resetMappings());
                                if (props.setShow) {
                                    props.setShow(false);
                                }
                                if (props.setSelectedPerson) {
                                    props.setSelectedPerson(undefined);
                                }
                            }
                        }
                    });
                }
            });
        }

        itemsOptions.push({
            label: t('globalData:buttons.dasOertliche'),
            icon: 'pi pi-external-link',
            command: () => {

                let name = '';
                let address = '';

                if (props.selectedPerson?.vorname) {
                    name = props.selectedPerson?.vorname;
                }

                if (props.selectedPerson?.name) {
                    if (name !== '') {
                        name += '+';
                    }
                    name += props.selectedPerson?.name;
                }

                if (props.selectedPerson && props.selectedPerson.adresse && props.selectedPerson.adresse.length > 0) {

                    let adresse = props.selectedPerson.adresse[0];

                    if (adresse.strassehausnummer) {
                        address = adresse.strassehausnummer;
                    }

                    if (adresse.ort) {
                        if (address !== '') {
                            address += '+';
                        }
                        address += adresse.ort;
                    }

                    if (adresse.plz) {
                        if (address !== '') {
                            address += '+';
                        }
                        address += adresse.plz;
                    }
                }

                window.open(`https://www.dasoertliche.de/?zvo_ok=0&kw=${name}&ci=${address}&form_name=search_nat`, '_blank');
            }
        });

        itemsOptions.push({
            label: t('globalData:buttons.exportOutlookContact'),
            icon: 'pi pi-file-export',
            command: () => {
                getOutlookContact([props.selectedPerson]);
            }
        });

        result.push(<>
            <Menu model={itemsActions} popup ref={menuActions} id="popup_menu_actions"/>
            <Button className={'p-button-text'} label={t('actions')} icon="pi pi-angle-down"
                    onClick={(event) => menuActions.current.toggle(event)}
                    aria-controls="popup_menu_actions" aria-haspopup/>

            <Menu model={itemsOptions} popup ref={menuOptions} id="popup_menu_options"/>
            <Button className={'p-button-text'} label={t('options')} icon="pi pi-angle-down"
                    onClick={(event) => menuOptions.current.toggle(event)}
                    aria-controls="popup_menu_options" aria-haspopup/>
        </>);

        return result;
    };

    const onCancel = () => {
        dispatch(resetMappings());
        if (props.setSelectedPerson) props.setSelectedPerson(undefined);
        if (props.setShow) props.setShow(false);
        removeIdFromHash();
    };

    return (
        <CustomDialog
            className={'edit-dialog'}
            contentClassName={'overflow-auto lg:overflow-hidden'}
            header={
                <div className={'flex'}>
                    <div className={'my-auto mr-2'}>{props.selectedPerson ?
                        <FontAwesomeIcon className="sidebar-icon" icon={faPen}/> :
                        <FontAwesomeIcon className="sidebar-icon" icon={faPlus}/>}</div>
                    <div>{t('globalData:persons.person')}</div>
                    <div>{props.selectedPerson ?
                        <ProcessUser projectId={props.projectId} id={props.selectedPerson.person_id}
                                     type={'person_id'}/> : undefined}</div>
                </div>}
            // headerType={props.selectedPerson ? CustomDialogHeaderType.Update : CustomDialogHeaderType.Create}
            onCancel={() => {
                if (isDirty) {
                    saveCheck(t, onCancel);
                } else {
                    onCancel();
                }
            }}
            visible={typeof props.show === 'boolean' ? props.show : false}
            customButtons={buttons}
            customButtonsBefore={true}
            buttonsInHeader={true}
            maximized={true}
            showFooter={true}
            formName={formName}
            hideSave={props.selectedPerson == null}
            onClickWithClose={() => {
                setClose(true);
            }}>
            <PersonEditForm id={formName}
                            data={props.selectedPerson}
                            setIsDirty={setIsDirty}
                            onFinished={(data) => {
                                if (close) {
                                    setClose(false);
                                    dispatch(resetMappings());
                                    if (props.setShow) {
                                        props.setShow(false);
                                    }
                                    if (props.setSelectedPerson) {
                                        props.setSelectedPerson(undefined);
                                    }
                                }

                                if (props.onFinished) {
                                    props.onFinished(data);
                                    removeIdFromHash();
                                }
                            }} projectId={props.projectId} commonData={props.commonData}
                            geoData={props.geoData}
                            createNew={createNew}
                            duplicateNew={duplicateNew}/></CustomDialog>
    );
};
